import Http from "./Http";
import { PLAN_UNDO_TIMEOUT_MS } from "../constants/http";

/**
 * Undo last save service
 *
 * Thin wrapper over the backend plan-undo APIs (last save only, no redo):
 *   GET  /plan-details/undo/{planId} — can the last save be undone? Changes nothing.
 *        Response: { canUndo, reason, savedBy, savedOn, lines: [{ coaId, departmentId, coaName, changeType }] }
 *   POST /plan-details/undo/{planId} — undo the last save.
 *        Response: { planId, linesRestored, lines: [...] }
 *        409 with { message } when it can't be undone (e.g. values changed since, unsaved changes).
 *   GET  /plan-details/undo-line/{planId}/{coaId}?departmentId=&toRowId= — can this line's last change be undone,
 *        or (with toRowId) the line be restored to before that change-log row? Same response shape, plus
 *        changesToUndo (how many of the line's changes go back).
 *   POST /plan-details/undo-line/{planId}/{coaId}?departmentId=&toRowId= — do it: the line and the lines depending
 *        on it become unsaved edits (drafts) to Save or Discard. 409 with { message } when it can't be done.
 *
 * Errors are re-thrown so callers can route them through `handleApiError`,
 * which shows a 409's message verbatim.
 */

const getUndoStatus = (planId) => Http.GET(`/plan-details/undo/${planId}`);

// Restoring a large save can take longer than the default request timeout, so undo gets its own limit.
const undoLastSave = (planId) => Http.POST(`/plan-details/undo/${planId}`, null, { timeout: PLAN_UNDO_TIMEOUT_MS });

/** toRowId omitted/null = the line's last change; otherwise restore to before that change-log row. */
const lineUndoUrl = (planId, coaId, departmentId, toRowId) => {
  const params = new URLSearchParams();
  if (departmentId != null) params.append("departmentId", departmentId);
  if (toRowId != null) params.append("toRowId", toRowId);
  const query = params.toString();
  return `/plan-details/undo-line/${planId}/${coaId}` + (query ? `?${query}` : "");
};

const getLineUndoStatus = (planId, coaId, departmentId, toRowId) =>
  Http.GET(lineUndoUrl(planId, coaId, departmentId, toRowId));

// Recalculates the line and its dependants, so it gets the same longer limit as undoLastSave.
const undoLineLastChange = (planId, coaId, departmentId, toRowId) =>
  Http.POST(lineUndoUrl(planId, coaId, departmentId, toRowId), null, { timeout: PLAN_UNDO_TIMEOUT_MS });

const planUndoService = {
  getUndoStatus,
  undoLastSave,
  getLineUndoStatus,
  undoLineLastChange,
};

export default planUndoService;
